import { hexalConfig, incyteConfig, novartisConfig } from '@common/config';
import { Client } from '@common/enums';
import { IInstanceConfig } from '@common/interfaces';
import { IMessage, INlpResponse, IQuestionOption, ITopic, MessageType } from '@common/types';
import { CLIENT_ENV } from '@constants/env';
import { SUPPORTED_LANGUAGES, TSupportedLanguage } from '@constants/languages';
import { clsx, type ClassValue } from 'clsx';
import { TFunction } from 'i18next';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getDefaultLanguage(): TSupportedLanguage {
  return SUPPORTED_LANGUAGES.find((lng) => lng.isDefault)!;
}

function getInstanceFromUrl() {
  const pathParts = window.location.pathname.split('/');
  const instanceIndex = pathParts.indexOf('instance');
  return instanceIndex !== -1 ? pathParts[instanceIndex + 1] : null;
}
function getCurrentInstance(): IInstanceConfig {
  if (CLIENT_ENV) {
    const instance = getInstanceFromUrl();
    console.info('[lib.utils] -> CLIENT_ENV: ', CLIENT_ENV);
    console.info('[lib.utils] -> Version: ', incyteConfig.devVersion, hexalConfig.devVersion);
    switch (CLIENT_ENV) {
      case Client.Incyte:
        if (instance) {
          const result = incyteConfig.instances.find((item) => item.name === instance);
          if (result) return result;
          throw new Error('[lib.utils]: No instance found');
        }
        return incyteConfig.instances[0];
      case Client.Hexal:
        if (instance) {
          const result = hexalConfig.instances.find((item) => item.name === instance);
          if (result) return result;
          throw new Error('[lib.utils]: No instance found');
        }
        return hexalConfig.instances[0];
      case Client.Novartis:
        if (instance) {
          const result = novartisConfig.instances.find((item) => item.name === instance);
          if (result) return result;
          throw new Error('[lib.utils]: No instance found');
        }
        return novartisConfig.instances[0];
      default:
        throw new Error('[lib.utils]: No instance found');
    }
  } else {
    throw new Error('[lib.utils]: No client detected');
  }
}
export const CURRENT_INSTANCE = getCurrentInstance();

export function getLangNamespace() {
  return CURRENT_INSTANCE.i18nNamespace;
}
export function getThemeName() {
  return CURRENT_INSTANCE.themeName;
}

export function getExactTopicFromTopicsList(topicsList: ITopic[], search: string): ITopic | null {
  let foundTopic: ITopic | null = null;
  topicsList.forEach((topic) => {
    if (topic.keywords?.some((keyword) => keyword.toLocaleLowerCase() === search)) {
      foundTopic = topic;
      return;
    }
  });
  return foundTopic;
}
export function findFirstTopicInQuery(query: string, topics: ITopic[]) {
  for (const topic of topics) {
    topic.keywords?.push(topic.name);
    if (topic.keywords?.length > 0) {
      for (const keyword of topic.keywords) {
        if (query.includes(keyword.toLowerCase())) {
          return topic;
        }
      }
    }
  }
}
export function convertQueryToMessage(query: string, total: number): IMessage {
  const id = ++total;
  return {
    id,
    message: query,
    type: MessageType.UserEntry,
  };
}
export function convertResponseToMessage(
  response: INlpResponse,
  total: number,
  t: TFunction
): IMessage {
  if (!response?.questionOptions) {
    const message = t('common.general_error');
    return {
      id: total,
      message: message || 'error',
      type: MessageType.Error,
    };
  }

  const firstOption = response.questionOptions[0];

  if (!firstOption) {
    return {
      id: total,
      message: t('chat.response.no_answer'),
      type: MessageType.NoAnswerResponse,
    };
  }

  const option = firstOption.optionType;
  if (option === 'CERTAIN_OPTION') {
    return questionOptionToMessage(firstOption, response.query!.id, t, total);
  }
  return {
    id: total,
    message: t('chat.response.not_sure'),
    choices: response.questionOptions,
    type: MessageType.ChoiceResponse,
  };
}
export function questionOptionToMessage(
  question: IQuestionOption,
  queryId: string,
  t: TFunction,
  total: number
): IMessage {
  const type = question.status === 'infoservice' ? MessageType.ForceMedInfo : MessageType.Response;
  return {
    id: total,
    questionQuery: question.actualQuery,
    questionId: question.questionId,
    message: question.answer || t('chat.response.no_text'),
    sourceDocuments: question.sourceDocuments,
    queryId,
    type,
  };
}
